import React, { useState, useCallback, useMemo, useRef } from 'react';

import { ITableComp } from 'views/components/table/type';
import { useQuery } from 'react-apollo';
import { GET_APPT_TYPES_CONFIG } from 'api/graphql/v2/queries/AppointmentTypes';
import PushableSideBar from 'views/components/ui/sidebar';
import { NEW_ABA_APPOINTMENT_SUBTYPE_CONFIG } from 'utils/constants/init';
import ConfigurationHeader from 'views/components/configurationHeader';
import TableComponent from 'views/components/table/Table';
import { useHistory } from 'react-router';
import { ABA_APPT_SUBTYPE_TITLE } from 'utils/constants/configurations';
import {
  IABAAppointmentSubType,
  ABAAppointmentSubType
} from 'model/abaAppointmentSubtype';
import { ABA_APPOINTMENT_SUBTYPES_COLUMNS } from 'views/components/table/tableColumns';
import Content from 'views/components/ui/content';
import ABAAppointmentSubTypeForm from 'views/components/forms/configurations/ABAAppointmentSubtypeForm.tsx';
import { GET_ABA_APPOINTMENT_SUBTYPES } from 'api/graphql/v2/queries/AppointmentTypes';
import { scrollToTop } from 'utils/math';
import {
  ISortValue,
  MAPPER_FUNCTION,
  mapToTableState
} from 'utils/mappers/configuration';
import { IABAAppointmentSubTypeConfig } from 'model/tableConfigurations';
import { TableType } from 'model/tableType';
import { EventTypes } from 'model/eventType';
import { GET_ABA_SUBTYPE_DATA } from 'api/graphql/v2/queries/Enums';

const ABAAppointmentSubTypes: React.FC<{}> = () => {
  const history = useHistory();
  const [currentForm, setCurrentForm] = useState(-1);
  const [sortValue, setSortValue] = useState<ISortValue>();
  const [searchValue, setSearchValue] = useState('');
  const formValues = useRef(NEW_ABA_APPOINTMENT_SUBTYPE_CONFIG);

  const { data: superTypes } = useQuery(GET_APPT_TYPES_CONFIG, {
    variables: {
      eventType: EventTypes.ABA
    },
    fetchPolicy: 'cache-and-network'
  });

  const { loading, error, data } = useQuery(GET_ABA_APPOINTMENT_SUBTYPES, {
    fetchPolicy: 'cache-and-network'
  });

  const { data: lookups } = useQuery(GET_ABA_SUBTYPE_DATA, {
    fetchPolicy: 'no-cache'
  });

  const addNew = useCallback(
    (data?: TableType) => {
      if (data) formValues.current = data as IABAAppointmentSubType;
      else formValues.current = NEW_ABA_APPOINTMENT_SUBTYPE_CONFIG;
      scrollToTop();
      setCurrentForm(formValues.current.id!);
    },
    [setCurrentForm]
  );

  const tableData = useMemo(() => {
    return data
      ? (mapToTableState(
          data.appointmentSubTypesConfig,
          addNew,
          MAPPER_FUNCTION.abaAppointmentSubTypes,
          searchValue,
          sortValue
        ) as IABAAppointmentSubTypeConfig[])
      : [];
  }, [data, addNew, searchValue, sortValue]);

  const onSearch = useCallback(
    value => {
      setSearchValue(value);
    },
    [setSearchValue]
  );

  const onSort = useCallback(
    (comp: ITableComp, reverse: boolean) => {
      setSortValue({ comp, reverse });
    },
    [setSortValue]
  );

  const onFormClose = useCallback(() => {
    formValues.current = NEW_ABA_APPOINTMENT_SUBTYPE_CONFIG;
    setCurrentForm(-1);
  }, [setCurrentForm]);

  const onGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  return (
    <>
      <PushableSideBar
        content={
          <ABAAppointmentSubTypeForm
            title={
              formValues.current.id
                ? `Edit ${ABA_APPT_SUBTYPE_TITLE}`
                : `New ${ABA_APPT_SUBTYPE_TITLE}`
            }
            initialValues={formValues.current}
            onFormClose={onFormClose}
            placesOfService={lookups?.placesOfService || []}
            superTypes={superTypes?.appointmentTypesConfig || []}
          />
        }
        visible={currentForm !== -1}
        pageComponent={
          <Content
            loading={loading}
            error={error}
            data={data}
            fullHeight={true}
          >
            {() => (
              <>
                <ConfigurationHeader
                  addNew={addNew}
                  onSearch={onSearch}
                  title={ABA_APPT_SUBTYPE_TITLE}
                  onClick={onGoBack}
                  visible={currentForm !== -1}
                />
                <TableComponent
                  headers={ABA_APPOINTMENT_SUBTYPES_COLUMNS}
                  data={tableData}
                  sort={onSort}
                  primarySortedKey={ABAAppointmentSubType.title}
                />
              </>
            )}
          </Content>
        }
      />
    </>
  );
};

export default ABAAppointmentSubTypes;
